import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Plus, Search, Sparkles } from 'lucide-react';
import type { ScopePaginationMeta } from '@ideoz/core';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@ideoz/ui';
import { ScopeCard, type ScopeCardScope } from './ScopeCard';
import { EmptyState } from '@ideoz/ui/components/molecules/EmptyState';
import type { ScopeSortOption } from '../useScopesList';

const ScopeSkeleton = () => (
  <div className="border border-border rounded-xl bg-background p-4 space-y-2">
    <div className="flex items-center justify-between">
      <Skeleton className="h-4 w-48" />
      <div className="flex gap-2">
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-5 w-20 rounded-full" />
      </div>
    </div>
    <Skeleton className="h-3 w-72" />
  </div>
);

/** Order the sort options appear in the dropdown. */
const SORT_OPTIONS: ScopeSortOption[] = [
  'newest',
  'oldest',
  'recentlyUpdated',
  'iceHigh',
  'iceLow',
];

/**
 * Numbered page list with ellipses: always show first + last, and a window of
 * pages around the current one. Small totals (<= 7) show every page.
 */
function buildPageItems(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items: (number | 'ellipsis')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) items.push('ellipsis');
  for (let p = start; p <= end; p += 1) items.push(p);
  if (end < total - 1) items.push('ellipsis');
  items.push(total);
  return items;
}

export interface ScopesListSectionProps {
  title: string;
  subtitle?: string;
  /** The current page of scopes (already server-filtered / sorted). */
  scopes: ScopeCardScope[];
  isLoading: boolean;
  onCardClick: (id: string) => void;
  onCardEdit: (scope: ScopeCardScope) => void;
  onCardDelete: (id: string) => void;
  /** Queues an ICE evaluation from the card's ⋯ menu. */
  onCardEvaluate: (id: string) => void;
  /** Opens the ICE details popup for a scored card. */
  onCardOpenIce: (id: string) => void;
  /** Scopes with an evaluation queued or running. */
  scoringScopeIds?: string[];
  onNewScope?: () => void;
  onSuggestScopes?: () => void;
  // Server-driven query controls.
  search: string;
  onSearchChange: (value: string) => void;
  /** Apply the typed search (fires on Enter / search-icon). */
  onSearchSubmit: () => void;
  /** Reset the search and refetch (empty-state "Clear search"). */
  onClearSearch: () => void;
  /** Whether a submitted search term is currently filtering the list. */
  isSearchActive: boolean;
  sort: ScopeSortOption;
  onSortChange: (option: ScopeSortOption) => void;
  pagination: ScopePaginationMeta | null;
  onPageChange: (page: number) => void;
}

export const ScopesListSection: React.FC<ScopesListSectionProps> = ({
  title,
  subtitle,
  scopes,
  isLoading,
  onCardClick,
  onCardEdit,
  onCardDelete,
  onCardEvaluate,
  onCardOpenIce,
  scoringScopeIds = [],
  onNewScope,
  onSuggestScopes,
  search,
  onSearchChange,
  onSearchSubmit,
  onClearSearch,
  isSearchActive,
  sort,
  onSortChange,
  pagination,
  onPageChange,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const PrevIcon = isRtl ? ChevronRight : ChevronLeft;
  const NextIcon = isRtl ? ChevronLeft : ChevronRight;

  const showPager = !!pagination && pagination.totalPages > 1;
  const pageItems =
    showPager && pagination ? buildPageItems(pagination.page, pagination.totalPages) : [];

  return (
    <>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">{title}</h2>
          {subtitle && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
          )}
        </div>
        <div className="flex flex-1 items-center justify-end gap-2 sm:flex-none">
          <div className="relative w-full max-w-xs shrink">
            <button
              type="button"
              onClick={onSearchSubmit}
              aria-label={t('workScopes.searchPlaceholder')}
              className="absolute start-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <Search size={16} />
            </button>
            <Input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSearchSubmit();
                }
              }}
              placeholder={t('workScopes.searchPlaceholder')}
              aria-label={t('workScopes.searchPlaceholder')}
              className="ps-9"
            />
          </div>
          <Select value={sort} onValueChange={(value) => onSortChange(value as ScopeSortOption)}>
            <SelectTrigger
              className="w-[180px] shrink-0"
              aria-label={t('workScopes.sort.label')}
              data-testid="scopes-sort"
            >
              <SelectValue placeholder={t('workScopes.sort.label')} />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {t(`workScopes.sort.${option}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      <div className="space-y-3">
        {isLoading ? (
          <>
            <ScopeSkeleton />
            <ScopeSkeleton />
            <ScopeSkeleton />
          </>
        ) : scopes.length === 0 ? (
          <div className="text-center text-gray-400 dark:text-gray-500">
            {isSearchActive ? (
              <EmptyState
                title={t('workScopes.empty.noMatchTitle')}
                description={t('workScopes.empty.noMatchDesc')}
                action={
                  <Button variant="secondary" size="sm" onClick={onClearSearch}>
                    {t('workScopes.clearSearch')}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title={t('workScopes.empty.noneTitle')}
                description={t('workScopes.empty.noneDesc')}
                action={
                  <Button variant="primary" size="sm" onClick={onNewScope}>
                    <Plus className="size-4" />
                    {t('workScopes.newScope')}
                  </Button>
                }
                secondaryAction={
                  <Button variant="secondary" size="sm" onClick={onSuggestScopes}>
                    <Sparkles className="size-4" />
                    {t('workScopes.addMultiple')}
                  </Button>
                }
              />
            )}
          </div>
        ) : (
          scopes.map((scope) => (
            <ScopeCard
              key={scope.id}
              scope={scope}
              onClick={onCardClick}
              onEdit={onCardEdit}
              onDelete={onCardDelete}
              onEvaluate={onCardEvaluate}
              onOpenIce={onCardOpenIce}
              isScoring={scoringScopeIds.includes(scope.id)}
            />
          ))
        )}
      </div>

      {showPager && pagination && (
        <div
          className="mt-6 flex items-center justify-between gap-3 border-t border-gray-100 pt-4 dark:border-gray-800"
          data-testid="scopes-pagination"
        >
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {t('workScopes.pagination.pageOf', {
              page: pagination.page,
              totalPages: pagination.totalPages,
            })}
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              disabled={!pagination.hasPrevious || isLoading}
              onClick={() => onPageChange(pagination.page - 1)}
              className="gap-1"
              data-testid="scopes-page-prev"
            >
              <PrevIcon className="size-4" />
              <span className="hidden sm:inline">{t('workScopes.pagination.prev')}</span>
            </Button>

            {pageItems.map((item, idx) =>
              item === 'ellipsis' ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="px-1 text-sm text-gray-400 dark:text-gray-500"
                  aria-hidden
                >
                  …
                </span>
              ) : (
                <Button
                  key={item}
                  variant={item === pagination.page ? 'primary' : 'outline'}
                  size="sm"
                  disabled={isLoading}
                  onClick={() => onPageChange(item)}
                  aria-current={item === pagination.page ? 'page' : undefined}
                  className="min-w-9 px-2"
                  data-testid={`scopes-page-${item}`}
                >
                  {item}
                </Button>
              )
            )}

            <Button
              variant="outline"
              size="sm"
              disabled={!pagination.hasNext || isLoading}
              onClick={() => onPageChange(pagination.page + 1)}
              className="gap-1"
              data-testid="scopes-page-next"
            >
              <span className="hidden sm:inline">{t('workScopes.pagination.next')}</span>
              <NextIcon className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default ScopesListSection;
